import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  MdCloudUpload,
  MdCheckCircle,
  MdError,
  MdInfo,
} from 'react-icons/md';
import {
  BUTTON_SIZE_SM,
  BUTTON_VARIANT_FILLED,
  BUTTON_VARIANT_OUTLINED,
  Button,
} from '@aibuild/ui';
import type { ArtifactKind, ProcessNodeDto, ProcessPortDto } from '@api/processes';
import {
  Dropzone,
  ErrorText,
  FormActions,
  FormRoot,
  HintText,
  InputHeader,
  InputMeta,
  InputRow,
  RequiredMark,
  TextField,
} from './RunConfigForm.styled';

// ── Public types ─────────────────────────────────────────────────────────────

/**
 * Form-side per-input value. The form keeps file objects local and hands
 * them up so the parent can drive the two-phase
 * {@code prepareRun → PUT → startPreparedRun} flow without the form
 * needing to know about presigned URLs or the run id.
 */
export type RunConfigFormValue =
  | { readonly kind: 'file'; readonly file: File }
  | { readonly kind: 'text'; readonly text: string };

/**
 * Backend-sourced error envelope for the form. When the server rejects a
 * Start request with {@code MISSING_REQUIRED_INPUT} (or similar per-input
 * fault), the parent surfaces the message + the offending port ids here
 * so the form can decorate the right rows. Per-row local validation
 * errors (missing file, missing text, ...) take precedence over this --
 * the server message only paints rows that don't already have a local
 * one. {@code missingInputs} carries Input-node OUTPUT port IDs to match
 * the keys in the new {@code /runs:prepare} + {@code /runs:start} API.
 */
export interface RunConfigFormInputError {
  readonly message: string;
  readonly missingInputs: readonly string[];
}

export interface RunConfigFormProps {
  /**
   * The Input nodes on the process — derived from the detail payload OR
   * the live editor graph (via {@link liveInputNodeToDto}). The form
   * renders one row per node, with a drop-zone or text field picked from
   * the node's required {@code dataType}.
   */
  inputNodes: readonly ProcessNodeDto[];
  /**
   * Parent collects the values map keyed by the Input node's OUTPUT port
   * id (one per Input node). Mirrors the new API shape — the parent then
   * fans them out into {@code prepareRun(fileInputs)} +
   * {@code startPreparedRun(inputs)}.
   */
  onSubmit: (values: Record<string, RunConfigFormValue>) => void;
  /** Called when the user dismisses the form (e.g. cancel button in a modal). */
  onCancel?: () => void;
  /** Whether the parent is currently submitting (locks the buttons). */
  isSubmitting?: boolean;
  /**
   * Last backend error from a failed Start. When set, rows whose port id
   * appears in {@link RunConfigFormInputError.missingInputs} render the
   * server message under the row.
   */
  inputError?: RunConfigFormInputError | null;
  /** Optional override for the Submit button label. */
  submitLabel?: string;
  /**
   * When false, file-typed Input rows render a disabled hint pointing the
   * user at the full Test Run dialog. Used by single-node "Run from here"
   * because the backend rejects file uploads on single-node runs — those
   * inputs must travel through the two-phase {@code /runs:prepare} +
   * {@code /runs:start} flow which only the full Test Run dialog drives.
   * Defaults to {@code true}.
   */
  fileUploadEnabled?: boolean;
  dataTestId?: string;
}

interface RowState {
  file?: File;
  text?: string;
  /** Local error message rendered below the row (validation / browser errors). */
  error?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const labelOf = (n: ProcessNodeDto): string =>
  n.label && n.label.trim().length > 0 ? n.label : 'Input';

const dataTypeOf = (n: ProcessNodeDto): ArtifactKind => {
  if (n.config.dataType) return n.config.dataType;
  // Fall back to the (single) output port's type, since Input nodes have
  // exactly one output that mirrors the data type.
  const port = n.ports.find((p) => p.direction === 'output');
  return port?.portType ?? 'MODEL_3D';
};

const outputPortIdOf = (n: ProcessNodeDto): string => {
  const port = n.ports.find((p: ProcessPortDto) => p.direction === 'output');
  if (!port) {
    throw new Error(
      `RunConfigForm: Input node '${n.nodeId}' has no output port — cannot key row.`,
    );
  }
  return port.portId;
};

const isTextType = (t: ArtifactKind): boolean =>
  t === 'TEXT' || t === 'NUMBER';

const buildValue = (state: RowState, isText: boolean): RunConfigFormValue | null => {
  if (isText) {
    if (typeof state.text === 'string' && state.text.length > 0) {
      return { kind: 'text', text: state.text };
    }
    return null;
  }
  if (state.file) {
    return { kind: 'file', file: state.file };
  }
  return null;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Sub-component: drop-zone for file-typed Input ────────────────────────────

interface UploadPanelProps {
  state: RowState;
  onChange: (next: RowState) => void;
  dataTestId: string;
  disabled?: boolean;
}

const UploadPanel: React.FC<UploadPanelProps> = ({
  state,
  onChange,
  dataTestId,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const pick = useCallback(
    (file: File) => {
      onChange({ file, error: undefined });
    },
    [onChange],
  );

  const onDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      e.preventDefault();
      setDragOver(true);
    },
    [disabled],
  );
  const onDragLeave = useCallback(() => setDragOver(false), []);
  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files?.[0];
      if (f) pick(f);
    },
    [disabled, pick],
  );
  const onFilePicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) pick(f);
    },
    [pick],
  );

  return (
    <Dropzone
      $dragOver={dragOver}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => !disabled && fileInputRef.current?.click()}
      data-testid={`${dataTestId}__dropzone`}
    >
      <input
        ref={fileInputRef}
        type="file"
        onChange={onFilePicked}
        disabled={disabled}
        data-testid={`${dataTestId}__file-input`}
      />
      {state.file ? (
        <>
          <MdCheckCircle size={24} />
          {state.file.name}
          <InputMeta>{formatFileSize(state.file.size)}</InputMeta>
        </>
      ) : (
        <>
          <MdCloudUpload size={24} />
          Drag a file here or click to browse
        </>
      )}
    </Dropzone>
  );
};

// ── Sub-component: per-Input row ─────────────────────────────────────────────

interface RowProps {
  node: ProcessNodeDto;
  state: RowState;
  onChange: (next: RowState) => void;
  /**
   * Backend-sourced error message for THIS row, or {@code null} when the
   * row has no server-side problem. Local errors in {@link RowState.error}
   * take precedence — see the note on {@link RunConfigFormInputError}.
   */
  remoteError: string | null;
  fileUploadEnabled: boolean;
  dataTestId: string;
}

const InputNodeRow: React.FC<RowProps> = ({
  node,
  state,
  onChange,
  remoteError,
  fileUploadEnabled,
  dataTestId,
}) => {
  const dataType = dataTypeOf(node);
  const required = node.config.required ?? true;
  const isText = isTextType(dataType);
  const fileDisabled = !isText && !fileUploadEnabled;
  const hasValue = buildValue(state, isText) != null;
  const visibleError =
    state.error ?? (hasValue ? null : remoteError) ?? null;

  return (
    <InputRow data-testid={dataTestId}>
      <InputHeader>
        <span>
          {labelOf(node)}
          {required ? (
            <RequiredMark
              aria-label="required"
              data-testid={`${dataTestId}__required`}
            >
              *
            </RequiredMark>
          ) : null}
        </span>
        <InputMeta data-testid={`${dataTestId}__type`}>{dataType}</InputMeta>
      </InputHeader>
      {node.config.text ? (
        <HintText data-testid={`${dataTestId}__hint`}>{node.config.text}</HintText>
      ) : null}

      {isText ? (
        <TextField
          value={state.text ?? ''}
          onChange={(e) => onChange({ text: e.target.value })}
          placeholder="Enter the text input for this node…"
          data-testid={`${dataTestId}__text`}
        />
      ) : null}

      {!isText && fileUploadEnabled ? (
        <UploadPanel
          state={state}
          onChange={onChange}
          dataTestId={dataTestId}
        />
      ) : null}

      {fileDisabled ? (
        <HintText data-testid={`${dataTestId}__file-disabled-hint`}>
          <MdInfo size={12} /> File inputs aren&apos;t supported here. Use Test
          Run in the side panel to upload a file for this input.
        </HintText>
      ) : null}

      {visibleError ? (
        <ErrorText data-testid={`${dataTestId}__error`}>
          <MdError size={12} /> {visibleError}
        </ErrorText>
      ) : null}
    </InputRow>
  );
};

// ── Form ─────────────────────────────────────────────────────────────────────

/**
 * Modal-friendly run-config form. Renders one row per Input node and
 * passes a {@link RunConfigFormValue} per port to the parent on submit:
 * <ul>
 *   <li>{@code TEXT} / {@code NUMBER} → a text field, value travels
 *       inline on {@code /runs:start}.</li>
 *   <li>file types → an inline drop-zone. The {@link File} is held
 *       locally and handed to the parent so the parent can mint a
 *       presigned PUT via {@code /runs:prepare} and upload directly to
 *       S3 before calling {@code /runs:start}.</li>
 * </ul>
 *
 * <p>Values are keyed by the Input node's OUTPUT port id — one port per
 * Input node — to match the new run-start API contract.</p>
 */
const RunConfigForm: React.FC<RunConfigFormProps> = ({
  inputNodes,
  onSubmit,
  onCancel,
  isSubmitting = false,
  inputError,
  submitLabel = 'Start run',
  fileUploadEnabled = true,
  dataTestId = 'run-config-form',
}) => {
  // Pre-resolve port ids per node (defensive: a malformed node would
  // otherwise crash mid-render). Doing it once here keeps the per-row
  // render cheap and gives us a single error site if a node is broken.
  const portIdByNode = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of inputNodes) m.set(n.nodeId, outputPortIdOf(n));
    return m;
  }, [inputNodes]);

  const missingInputsSet = useMemo(
    () => new Set(inputError?.missingInputs ?? []),
    [inputError?.missingInputs],
  );
  const remoteErrorFor = (portId: string): string | null =>
    missingInputsSet.has(portId) ? (inputError?.message ?? null) : null;

  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const seed: Record<string, RowState> = {};
    for (const n of inputNodes) seed[n.nodeId] = {};
    return seed;
  });

  // Keep `rows` in sync with `inputNodes`. The parent passes a freshly
  // computed list every time the graph changes (Input nodes added/removed,
  // or their dataType edited), so the one-shot init isn't enough — without
  // this reseed, removing an Input would leave its stale value in the
  // values map we hand back on submit.
  // We use the React docs' "store information from previous renders"
  // pattern (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // rather than a `useEffect` so the lint rule banning synchronous
  // setState-in-effect is happy and we avoid an extra render. Existing
  // entries are preserved for nodes that still exist.
  const [seenNodes, setSeenNodes] = useState(inputNodes);
  if (seenNodes !== inputNodes) {
    setSeenNodes(inputNodes);
    setRows((prev) => {
      const next: Record<string, RowState> = {};
      for (const n of inputNodes) {
        next[n.nodeId] = prev[n.nodeId] ?? {};
      }
      return next;
    });
  }

  const setRow = useCallback(
    (nodeId: string, next: RowState) =>
      setRows((curr) => ({ ...curr, [nodeId]: next })),
    [],
  );

  const allValuesValid = useMemo(() => {
    for (const node of inputNodes) {
      const required = node.config.required ?? true;
      if (!required) continue;
      const isText = isTextType(dataTypeOf(node));
      // File-typed required inputs whose upload UI is disabled cannot be
      // satisfied from this form — block submit so the user has to
      // either deselect "required" or switch to the Test Run dialog.
      if (!isText && !fileUploadEnabled) return false;
      const value = buildValue(rows[node.nodeId] ?? {}, isText);
      if (!value) return false;
    }
    return true;
  }, [fileUploadEnabled, inputNodes, rows]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // Mirror the disabled-button gate as a defensive check — a parent
      // could trigger submit programmatically, and Enter inside an input
      // shouldn't be able to slip past the validation either.
      if (!allValuesValid || isSubmitting) return;
      const values: Record<string, RunConfigFormValue> = {};
      for (const node of inputNodes) {
        const isText = isTextType(dataTypeOf(node));
        const v = buildValue(rows[node.nodeId] ?? {}, isText);
        if (v) {
          const portId = portIdByNode.get(node.nodeId);
          if (portId) values[portId] = v;
        }
      }
      onSubmit(values);
    },
    [allValuesValid, inputNodes, isSubmitting, onSubmit, portIdByNode, rows],
  );

  return (
    <FormRoot onSubmit={handleSubmit} data-testid={dataTestId}>
      {inputNodes.length === 0 ? (
        <HintText data-testid={`${dataTestId}__empty`}>
          This process has no Input nodes — runs can be started directly.
        </HintText>
      ) : null}
      {inputNodes.map((node) => {
        const portId = portIdByNode.get(node.nodeId) ?? node.nodeId;
        return (
          <InputNodeRow
            key={node.nodeId}
            node={node}
            state={rows[node.nodeId] ?? {}}
            onChange={(next) => setRow(node.nodeId, next)}
            remoteError={remoteErrorFor(portId)}
            fileUploadEnabled={fileUploadEnabled}
            dataTestId={`${dataTestId}__row-${node.nodeId}`}
          />
        );
      })}
      <FormActions>
        {onCancel ? (
          <Button
            variant={BUTTON_VARIANT_OUTLINED}
            size={BUTTON_SIZE_SM}
            onClick={onCancel}
            disabled={isSubmitting}
            dataTestId={`${dataTestId}__cancel`}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          type="submit"
          variant={BUTTON_VARIANT_FILLED}
          size={BUTTON_SIZE_SM}
          disabled={!allValuesValid || isSubmitting}
          dataTestId={`${dataTestId}__submit`}
        >
          {isSubmitting ? 'Starting…' : submitLabel}
        </Button>
      </FormActions>
    </FormRoot>
  );
};

export default RunConfigForm;
